import { h, ComponentChildren, ComponentType } from "preact";
import { useMemo, useRef, useState } from "preact/hooks";

import classname from "../../foundation/utils/classname";

import IconArrowDown from "../Icons/IconArrowDown";
import Portal from "../Portal";

import DropdownItem from "./DropdownItem";
import DropdownParentItem from "./DropdownItem/DropdownParentItem";
import DropdownOptions, { OptionsProps } from "./DropdownOptions";

import cloneDropdownItems from "./utils/cloneDropdownItems";
import findSelectedItem from "./utils/findSelectedItem";

import styles from "./Dropdown.module.css";

interface DropdownProps<V> {
    /** Classname for dropdown */
    class?: string;
    /** Whether DropdownOptions is visible or not */
    optionsVisible?: boolean;
    /** Classname for label */
    labelClass?: string;
    /** Destination node for `DropdownOptions` to be rendered. Default: `document.body` */
    portalInto?: string | HTMLElement;
    /** If not empty, show a default label, until a `Dropdown.Item` is selected */
    defaultLabel?: ComponentChildren;
    /** If not empty, this is used as `title` of dropdown until a `Dropdown.Item` with a string label is selected */
    defaultTitle?: string;
    chevronComponent?: ComponentType<{ class?: string; width?: number; color?: string; }>;
    chevronClass?: string;
    /** Change color of chevron */
    chevronColor?: string;
    /** Whether to show a check mark next to the selected item. Default: `true` */
    showCheck?: boolean;
    /** Change visibility of label. Default: `true` */
    showLabel?: boolean;
    /** Change visibility of chevron. Default: `true` */
    showChevron?: boolean;
    /** Enables displaying `label` props of selected `Dropdown.Item` as `Dropdown` label. Default: `true` */
    showSelectedAsLabel?: boolean;
    /** Enable multi selection. Default: `false` */
    multiSelect?: boolean;
    /** Disable dropdown click */
    disabled?: boolean;
    /** Props for `DropdownOptions` component */
    options?: OptionsProps;
    /** Props for suboptions */
    suboptionsOptions?: Pick<OptionsProps, "class" | "alignSuboptionsToBottom" | "left" | "top" | "fixedTop">;
    /** Keep the dropdown menu open after selection */
    keepOpenAfterSelection?: boolean;
    /** `Dropdown.Item` instances to be displayed when `Dropdown` is clicked. */
    children?: ComponentChildren;
    /** Setter for optionsVisible value */
    setOptionsVisible?: (visible: boolean) => void;
    /** Callback to be called when an `Dropdown.Item` is selected. */
    onChange?: (value: V) => void;
    /** Callback to be called on Dropdown state change */
    onDropdownStateChange?: (open: boolean) => void;
    /** Callback to be called when Dropdown is clicked. This gets disabled when `disabled` prop is true. */
    onDropdownClick?: (event: Event) => void;
    onMouseDown?: (event: Event) => void;
}

/**
 * The styles that can be customized can be provided by the following CSS variables:
 *  - `--dropdown-width` (default: `auto`): Changes the width of the dropdown.
 *  - `--dropdown-height` (default: `28px`): Changes the height of the dropdown.
 *  - `--dropdown-bg` (default: ` var(--karl)`): Changes the background color of dropdown.
 *  - `--dropdown-bg-hover` (default: `#e0dfe0` (blent color karl and gandalf-15pc)): Changes the background color of dropdown when it's hovered.
 *  - `--dropdown-bg-active` (default: `#d4d1d4` (blent color karl and gandalf-30pc)): Changes the background color of dropdown when the items are being displayed.
 */
function Dropdown<V>({
    class: cssClass,
    optionsVisible,
    labelClass,
    portalInto = document.body,
    defaultLabel,
    defaultTitle,
    chevronComponent: ChevronComponent = IconArrowDown,
    chevronClass,
    chevronColor,
    showCheck = true,
    showLabel = true,
    showChevron = true,
    showSelectedAsLabel = true,
    multiSelect = false,
    disabled,
    options: optionsProps,
    suboptionsOptions: suboptionsOptionsProps,
    keepOpenAfterSelection = false,
    children,
    setOptionsVisible,
    onChange,
    onDropdownStateChange,
    onDropdownClick,
    onMouseDown
}: DropdownProps<V>) {
    const [internalVisible, setInternalVisible] = useState(false);
    const [subOptionsParent, setSubOptionsParent] = useState<DropdownParentItem | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);

    function isOptionsOpen() {
        if (optionsVisible !== undefined) {
            return optionsVisible;
        }

        return internalVisible;
    }

    function setOptionsOpen(open: boolean) {
        if (optionsVisible !== undefined) {
            setOptionsVisible?.(open);
            return;
        }

        setInternalVisible(open);
    }

    function getLabelAndTitle() {
        let label = defaultLabel;
        let title = defaultTitle;

        if (!showSelectedAsLabel) {
            return { label, title };
        }

        const selectedItem = findSelectedItem<V>(children);

        if (!multiSelect && selectedItem) {
            const { dropdownLabel, label: itemLabel } = selectedItem.props;
            label = dropdownLabel || itemLabel;
        }

        if (!multiSelect && selectedItem && typeof selectedItem.props.label === "string") {
            title = selectedItem.props.label;
        } else if (!defaultTitle && typeof defaultLabel === "string") {
            title = defaultLabel;
        }

        return { label, title };
    }

    function handleSetOpen(value: boolean) {
        setOptionsOpen(value);
        onDropdownStateChange?.(value);
    }

    function hideOptions() {
        handleSetOpen(false);
        setSubOptionsParent(null);
    }

    function toggleOptions(ev: Event) {
        if (disabled) {
            return;
        }

        if (!onDropdownClick) {
            ev.stopPropagation();
            handleSetOpen(!isOptionsOpen());
        } else {
            onDropdownClick(ev);
        }
    }

    function handleItemSelect(value: V) {
        if (!multiSelect && !keepOpenAfterSelection) {
            hideOptions();
        }

        onChange?.(value);
    }

    function handleItemMouseEnter(hoveredParentItem?: DropdownParentItem) {
        if (!hoveredParentItem) {
            setSubOptionsParent(null);
            return;
        }

        setSubOptionsParent(hoveredParentItem);
    }

    function handleOptionsClick() {
        setSubOptionsParent(null);
    }

    function renderOptions(renderInto?: DropdownParentItem | null) {
        const childrenItems = renderInto ? renderInto.props.children : children;
        const subOptions = !!renderInto;
        const suboptionsProps = suboptionsOptionsProps ?? optionsProps;

        const items = cloneDropdownItems(childrenItems, {
            showCheck,
            subItem: subOptions,
            onSelect: handleItemSelect,
            onHover: handleItemMouseEnter
        });

        const parentRef = renderInto
            ? (renderInto.ref.current as HTMLDivElement)
            : dropdownRef.current;

        const currentOptionsProps = subOptions ? suboptionsProps : optionsProps;
        return (
            <Portal into={portalInto}>
                <DropdownOptions
                    {...currentOptionsProps}
                    parentRef={parentRef}
                    subOptions={subOptions}
                    onClick={handleOptionsClick}
                    onClose={hideOptions}>
                    {items}
                </DropdownOptions>
            </Portal>
        );
    }

    function renderSubOptions() {
        return renderOptions(subOptionsParent);
    }

    const classnames = classname(styles.dropdown, "noSelect", cssClass, {
        [styles.open]: isOptionsOpen(),
        [styles.disabled]: disabled
    });
    const { label, title } = useMemo(getLabelAndTitle, [
        showSelectedAsLabel,
        defaultLabel,
        defaultTitle,
        multiSelect,
        children
    ]);
    const tabIndex = disabled ? undefined : 0;
    const subOptionsOpen = !!subOptionsParent;

    return (
        <div
            ref={dropdownRef}
            class={classnames}
            title={title}
            tabIndex={tabIndex}
            onClick={toggleOptions}
            onMouseDown={onMouseDown}>
            {showLabel && <span class={classname("ellipsis", labelClass)}>{label}</span>}
            {showChevron && <ChevronComponent class={classname("chevron", chevronClass)} width={9} color={chevronColor}/>}
            {isOptionsOpen() && renderOptions()}
            {subOptionsOpen && renderSubOptions()}
        </div>
    );
}

Dropdown.Item = DropdownItem;
Dropdown.ParentItem = DropdownParentItem;

export { DropdownProps };
export default Dropdown;
